import './LoginPage.css';

import React, { useEffect, useState } from 'react';
import { config } from '../config';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { getOrganizationImagePathWithApiKey, getOrganizationNameWithApiKey } from '../services/b2b-users-apiService';
import { Box, Card, CardMedia, Skeleton } from '@mui/material';
import CustomButton from './b2b-common/CustomButton';
import { generateTextField } from './b2b-common/helpers';
import useB2BTranslation from './b2b-common/hooks/useB2BTranslation';

export const Logo = ({ altText, organizationUuid }) => {
    const [orgImagePath, setOrgImagePath] = useState(null);

    useEffect(() => {
        const abortController = new AbortController();

        const fetchOrganizationImage = async () => {
            try {
                const orgImage = await getOrganizationImagePathWithApiKey(organizationUuid, { signal: abortController.signal });
                const cacheBuster = Date.now(); // get the current time
                setOrgImagePath(`${config.baseB2B_Images}${orgImage}?${cacheBuster}`);
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('Fetch aborted');
                } else {
                    console.error('Failed to download organization image:', error.message);
                }
            }
        };

        if (organizationUuid) {
            fetchOrganizationImage();
        }

        return () => {
            abortController.abort();
        };
    }, [organizationUuid]);

    return (
        <div className="logo-container">
            {orgImagePath ? (
                <CardMedia
                    key={orgImagePath}
                    component="img"
                    image={orgImagePath}
                    alt={altText}
                    className='logo'
                />
            ) : (
                <Skeleton variant="rounded" width={220} height={220} animation="wave" className='logo-skeleton' />
            )}
        </div>
    );
};

const LoginForm = ({ onLogin, organizationUuid }) => {
    const { t } = useB2BTranslation('other');
    const [username, setUsername] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [error, setError] = React.useState(null);

    const handleLogin = async (e) => {
        e.preventDefault(); // Prevent the default form submission behavior
        try {
            const requestBody = {
                username: username,
                password: password,
            };
            if (organizationUuid !== null) {
                requestBody.organizationUuid = organizationUuid;
            }
            const response = await axios.post(`${config.baseB2B_URL}/b2b/login`, requestBody);
            const responseData = response.data;
            // Pass the login data up to the parent component
            onLogin(responseData);
        } catch (error) {
            // Handle error (e.g., display error message)
            console.error(t('Login failed:'), error);
            setError('Invalid credentials. Please try again.');
        }
    };

    return (
        <form onSubmit={handleLogin} className='b2b-login-form'>
            <div className="login-fields">
                {generateTextField(t("User Name"), username, "user-name", false, (e) => setUsername(e.target.value), { required: true })}
                {generateTextField(t("Password"), password, "password", false, (e) => setPassword(e.target.value), { required: true, type: 'password' })}
            </div>
            <div className="login-buttons">
                <CustomButton type="submit" label={t("Login")} />
            </div>
            {error && (
                <div className="error-message-container">
                    <div className="error-message">{error}</div>
                </div>
            )}
            <br />
        </form>
    );
};

const LoginPage = ({ onLogin }) => {
    const { t } = useB2BTranslation('other');
    const location = useLocation();
    const [organizationUuid, setOrganizationUuid] = React.useState(null);
    const [orgName, setOrgName] = React.useState('');

    const getOrganizationName = async (organizationUUID) => {
        try {
            const fetchedName = await getOrganizationNameWithApiKey(organizationUUID);
            setOrgName(fetchedName);
        } catch (error) {
            console.error(t('Failed to fetched organization name:'), error.message);
        }
    };

    useEffect(() => {
        if (organizationUuid) {
            getOrganizationName(organizationUuid);
        }
    }, [organizationUuid]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const uuidFromParams = params.get('organizationUuid');
        if (!(uuidFromParams === null || uuidFromParams === undefined || uuidFromParams === '')) {
            setOrganizationUuid(uuidFromParams);
        }
    }, [location]);

    return (
        <Card className={`login-container ${organizationUuid ? 'login-container-with-image' : ''}`}>
            { organizationUuid && (
                <div className="login-image-section">
                    <Logo altText="Back2Blu" organizationUuid={organizationUuid} />
                </div>
            )}
            <Box className="login-form-section" gap={1} style={{ width: '25%' }}>
                {/* <h3>{t('Back2Blu Administration')}</h3> */}
                <CardMedia
                    component="img"
                    image="/images/imagotipo-app-positivo.6706641.svg"
                    alt="Your Image Description"
                    className="custom-image"
                    sx={{ width: 180, alignSelf: 'flex-start' }}
                />
                { organizationUuid && (
                    <h1>{orgName}</h1>
                )}
                <LoginForm onLogin={onLogin} organizationUuid={organizationUuid} />
            </Box>
        </Card>
    );
};

export default LoginPage;
